import React, { useState, useEffect } from 'react';
import { 
  User, 
  Settings, 
  Bell, 
  ShieldCheck, 
  LogOut, 
  Package, 
  MessageCircle, 
  Edit2, 
  Camera, 
  Save, 
  X, 
  Award, 
  Heart, 
  Sparkles, 
  Moon, 
  Sun, 
  Palette, 
  Download, 
  Upload, 
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';
import { LogoutConfirmationDialog } from './ConfirmationDialogs';
import { FormFieldStatus } from './FeedbackIndicators';
import { LoadingButton } from './LoadingStates';
import { showToast } from '../utils/toast';
import { persistentState } from '../utils/stateManager';
import ChatModal from './Profile/ChatModal';
import NotificationsPanel from './Profile/NotificationsPanel';
import OrdersPanel from './Profile/OrdersPanel';
import ExpertVerification from './ExpertVerification';
import ExpertDashboard from './ExpertDashboard';

interface ProfileProps {
  onNavigate: (section: string, data?: any) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  hasUnsavedChanges: boolean;
}

const Profile: React.FC<ProfileProps> = ({ 
  onNavigate, 
  setHasUnsavedChanges,
  hasUnsavedChanges
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showExpertVerification, setShowExpertVerification] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [initialChatId, setInitialChatId] = useState<string | undefined>(undefined);
  
  const { user, signOut } = useAuth();
  const { profile, loading, updateProfile, refreshProfile } = useProfile();
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    bio: '',
    location: '',
    healthGoals: [] as string[],
    interests: [] as string[]
  });
  
  // Theme state
  const [theme, setTheme] = useState(() => {
    const savedTheme = persistentState.theme.get();
    return {
      mode: savedTheme.theme || 'light',
      accentColor: savedTheme.accentColor || 'green'
    };
  });
  
  // Load profile data into form
  useEffect(() => {
    if (profile) {
      setFormData({
        fullName: profile.full_name || '',
        username: profile.username || '',
        bio: profile.bio || '',
        location: profile.location || '',
        healthGoals: profile.health_goals || [],
        interests: profile.interests || []
      });
    }
  }, [profile]);
  
  // Check URL parameters for initial tab
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab && ['profile', 'notifications', 'orders', 'settings', 'expert'].includes(tab)) {
      setActiveTab(tab);
    }
    
    // Check for chat parameter
    const chatId = urlParams.get('chat');
    if (chatId) {
      setInitialChatId(chatId);
      setShowChatModal(true);
    }
    
    // Check for order parameter
    const orderId = urlParams.get('order');
    if (orderId) {
      setActiveTab('orders');
    }
  }, []);
  
  // Update unsaved changes flag
  useEffect(() => {
    if (!profile) return;
    
    const hasChanges = 
      formData.fullName !== (profile.full_name || '') ||
      formData.username !== (profile.username || '') ||
      formData.bio !== (profile.bio || '') ||
      formData.location !== (profile.location || '') ||
      JSON.stringify(formData.healthGoals) !== JSON.stringify(profile.health_goals || []) ||
      JSON.stringify(formData.interests) !== JSON.stringify(profile.interests || []);
    
    setHasUnsavedChanges(hasChanges);
  }, [formData, profile, setHasUnsavedChanges]);
  
  const handleSaveProfile = async () => {
    // Validate form
    const errors: {[key: string]: string} = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (formData.username.trim() && !/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      errors.username = 'Username must be 3-20 characters and contain only letters, numbers, and underscores';
    }
    
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      showToast.form.validationError('profile');
      return;
    }
    
    setIsSaving(true);
    
    try {
      await updateProfile({
        full_name: formData.fullName,
        username: formData.username || null,
        bio: formData.bio || null,
        location: formData.location || null,
        health_goals: formData.healthGoals,
        interests: formData.interests
      });
      
      setIsEditing(false);
      setHasUnsavedChanges(false);
      showToast.profile.updated();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast.system.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancelEdit = () => {
    if (profile) {
      setFormData({
        fullName: profile.full_name || '',
        username: profile.username || '',
        bio: profile.bio || '',
        location: profile.location || '',
        healthGoals: profile.health_goals || [],
        interests: profile.interests || []
      });
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };
  
  const handleLogout = async () => {
    try {
      await signOut();
      onNavigate('discover');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const handleThemeChange = (mode: 'light' | 'dark') => {
    setTheme(prev => ({ ...prev, mode }));
    
    // Apply theme to document
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save theme preference
    persistentState.theme.set({ theme: mode, accentColor: theme.accentColor });
    
    showToast.profile.themeChanged(mode);
  };
  
  const handleAccentColorChange = (color: string) => {
    setTheme(prev => ({ ...prev, accentColor: color }));
    
    // Apply accent color
    const colorMap = {
      green: '#16a34a',
      blue: '#3b82f6',
      purple: '#8b5cf6',
      orange: '#f59e0b',
      red: '#ef4444',
      pink: '#ec4899'
    };
    
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colorMap[color as keyof typeof colorMap]);
    
    // Save theme preference
    persistentState.theme.set({ theme: theme.mode, accentColor: color });
    
    showToast.profile.updated();
  };
  
  const handleExpertVerificationSubmit = async (data: any) => {
    try {
      // In a real app, this would submit to Supabase
      console.log('Expert verification data:', data);
      
      // Update profile to show pending verification
      await updateProfile({
        is_expert: true,
        verification_status: 'pending'
      });
      
      await refreshProfile();
      
      showToast.expert.verificationSubmitted();
    } catch (error) {
      console.error('Error submitting expert verification:', error);
      showToast.system.error('Failed to submit verification');
    }
  };
  
  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your profile...</p>
        </div>
      </div>
    );
  }
  
  // Render expert dashboard if user is an expert
  if (profile?.is_expert && activeTab === 'expert') {
    return <ExpertDashboard onNavigate={onNavigate} />;
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20 lg:pb-0">
      {/* Header */}
      <div className="glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My Profile</h1>
              <p className="text-gray-600 dark:text-gray-300">Manage your account and preferences</p>
            </div>
            <div className="flex items-center space-x-3">
              {profile?.is_expert && (
                <div className="flex items-center space-x-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 px-3 py-2 rounded-lg">
                  <Award size={16} />
                  <span className="text-sm font-medium">
                    {profile.verification_status === 'verified' ? 'Verified Expert' : 'Expert Status: ' + profile.verification_status}
                  </span>
                </div>
              )}
              <button
                onClick={() => setShowChatModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <MessageCircle size={20} />
                <span>Messages</span>
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-1 glass rounded-2xl p-2 border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'profile'
                  ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
              }`}
            >
              <User size={18} />
              <span>Profile</span>
            </button>
            
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'notifications'
                  ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
              }`}
            >
              <Bell size={18} />
              <span>Notifications</span>
            </button>
            
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'orders'
                  ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
              }`}
            >
              <Package size={18} />
              <span>Orders</span>
            </button>
            
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'settings'
                  ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
              }`}
            >
              <Settings size={18} />
              <span>Settings</span>
            </button>
            
            {profile?.is_expert && (
              <button
                onClick={() => setActiveTab('expert')}
                className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'expert'
                    ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                }`}
              >
                <Award size={18} />
                <span>Expert Dashboard</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="card-base card-padding-md">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-4xl font-bold">
                    {profile?.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                  </div>
                  {isEditing && (
                    <button className="absolute bottom-0 right-0 w-8 h-8 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-lg border border-gray-200 dark:border-gray-700">
                      <Camera size={16} className="text-gray-600 dark:text-gray-300" />
                    </button>
                  )}
                </div>
                
                {/* Profile Info */}
                <div className="flex-1">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          value={formData.fullName}
                          onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                          className={`input-base ${formErrors.fullName ? 'input-error' : ''}`}
                        />
                        {formErrors.fullName && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.fullName}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Username
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">@</span>
                          <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => setFormData({...formData, username: e.target.value})}
                            className={`input-base pl-8 ${formErrors.username ? 'input-error' : ''}`}
                          />
                        </div>
                        {formErrors.username && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.username}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Bio
                        </label>
                        <textarea
                          value={formData.bio}
                          onChange={(e) => setFormData({...formData, bio: e.target.value})}
                          rows={3}
                          className="textarea-base"
                          placeholder="Tell us about yourself..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => setFormData({...formData, location: e.target.value})}
                          className="input-base"
                          placeholder="e.g., Accra, Ghana"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          onClick={handleCancelEdit}
                          className="btn-ghost"
                          disabled={isSaving}
                        >
                          <X size={18} className="mr-1" />
                          Cancel
                        </button>
                        <LoadingButton
                          onClick={handleSaveProfile}
                          isLoading={isSaving}
                          loadingText="Saving..."
                          disabled={isSaving}
                          className="btn-primary"
                        >
                          <Save size={18} className="mr-1" />
                          Save Profile
                        </LoadingButton>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {profile?.full_name || 'User'}
                        </h2>
                        <button
                          onClick={() => setIsEditing(true)}
                          className="btn-ghost btn-sm"
                        >
                          <Edit2 size={16} className="mr-1" />
                          Edit
                        </button>
                      </div>
                      
                      {profile?.username && (
                        <p className="text-gray-600 dark:text-gray-400 mb-2">@{profile.username}</p>
                      )}
                      
                      {profile?.bio && (
                        <p className="text-gray-700 dark:text-gray-300 mb-4">{profile.bio}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {profile?.location && (
                          <span className="badge-secondary">
                            📍 {profile.location}
                          </span>
                        )}
                        
                        {profile?.is_expert && (
                          <span className="badge-success">
                            ✓ Expert
                          </span>
                        )}
                        
                        <span className="badge-info">
                          🗓️ Joined {new Date(profile?.created_at || '').toLocaleDateString()}
                        </span>
                      </div>
                      
                      {!profile?.is_expert && profile?.verification_status !== 'pending' && (
                        <button
                          onClick={() => setShowExpertVerification(true)}
                          className="btn-secondary btn-sm flex items-center space-x-1"
                        >
                          <Award size={16} />
                          <span>Become an Expert</span>
                        </button>
                      )}
                      
                      {profile?.verification_status === 'pending' && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mt-4">
                          <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                            <span className="font-medium">Expert verification pending</span> - Our team is reviewing your application.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Health Goals & Interests */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Health Goals */}
              <div className="card-base card-padding-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Health Goals</h3>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm"
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-2">
                    {['Weight Management', 'Fitness', 'Nutrition', 'Mental Wellbeing', 'Sleep Improvement', 'Stress Reduction', 'Chronic Condition Management'].map((goal) => (
                      <label key={goal} className="flex items-center space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.healthGoals.includes(goal)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, healthGoals: [...formData.healthGoals, goal]});
                            } else {
                              setFormData({...formData, healthGoals: formData.healthGoals.filter(g => g !== goal)});
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-gray-700 dark:text-gray-300">{goal}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {profile?.health_goals && profile.health_goals.length > 0 ? (
                      profile.health_goals.map((goal, index) => (
                        <span key={index} className="badge-primary">
                          {goal}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm italic">No health goals set</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Interests */}
              <div className="card-base card-padding-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Interests</h3>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm"
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-2">
                    {['Yoga', 'Running', 'Meditation', 'Healthy Cooking', 'Herbal Medicine', 'Traditional Healing', 'Mental Health', 'Nutrition'].map((interest) => (
                      <label key={interest} className="flex items-center space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(interest)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, interests: [...formData.interests, interest]});
                            } else {
                              setFormData({...formData, interests: formData.interests.filter(i => i !== interest)});
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-gray-700 dark:text-gray-300">{interest}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {profile?.interests && profile.interests.length > 0 ? (
                      profile.interests.map((interest, index) => (
                        <span key={index} className="badge-secondary">
                          {interest}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm italic">No interests set</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Account Stats */}
            <div className="card-base card-padding-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Account Stats</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-4 text-center">
                  <Heart className="mx-auto text-purple-600 dark:text-purple-400 mb-2" size={24} />
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">0</p>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Reactions</p>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4 text-center">
                  <MessageCircle className="mx-auto text-blue-600 dark:text-blue-400 mb-2" size={24} />
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">0</p>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Comments</p>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-4 text-center">
                  <Package className="mx-auto text-green-600 dark:text-green-400 mb-2" size={24} />
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">0</p>
                  <p className="text-sm text-green-600 dark:text-green-400">Orders</p>
                </div>
                
                <div className="bg-orange-50 dark:bg-orange-900/30 rounded-xl p-4 text-center">
                  <Sparkles className="mx-auto text-orange-600 dark:text-orange-400 mb-2" size={24} />
                  <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">0</p>
                  <p className="text-sm text-orange-600 dark:text-orange-400">Points</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'notifications' && (
          <NotificationsPanel onNavigate={onNavigate} />
        )}
        
        {activeTab === 'orders' && (
          <OrdersPanel onNavigate={onNavigate} />
        )}
        
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h2>
            
            {/* Theme Settings */}
            <div className="card-base card-padding-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Appearance</h3>
              
              <div className="space-y-6">
                {/* Theme Mode */}
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Theme</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleThemeChange('light')}
                      className={`p-4 border-2 rounded-xl flex items-center space-x-3 transition-all ${
                        theme.mode === 'light' 
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <Sun className="text-amber-500" size={24} />
                      <div className="text-left">
                        <p className="font-medium dark:text-gray-100">Light Mode</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Bright and clear</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => handleThemeChange('dark')}
                      className={`p-4 border-2 rounded-xl flex items-center space-x-3 transition-all ${
                        theme.mode === 'dark' 
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <Moon className="text-indigo-600 dark:text-indigo-400" size={24} />
                      <div className="text-left">
                        <p className="font-medium dark:text-gray-100">Dark Mode</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Easy on the eyes</p>
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* Accent Color */}
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Accent Color</h4>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { name: 'green', color: '#16a34a' },
                      { name: 'blue', color: '#3b82f6' },
                      { name: 'purple', color: '#8b5cf6' },
                      { name: 'orange', color: '#f59e0b' },
                      { name: 'red', color: '#ef4444' },
                      { name: 'pink', color: '#ec4899' }
                    ].map(({ name, color }) => (
                      <button
                        key={name}
                        onClick={() => handleAccentColorChange(name)}
                        className={`w-10 h-10 rounded-full transition-all ${
                          theme.accentColor === name ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-600 scale-110' : ''
                        }`}
                        style={{ backgroundColor: color }}
                        aria-label={`${name} theme`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Account Settings */}
            <div className="card-base card-padding-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Account</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-gray-700 dark:text-gray-300">Email Address</p>
                  <p className="font-medium dark:text-gray-100">{user.email}</p>
                </div>
                
                <div>
                  <button className="btn-secondary">
                    Change Password
                  </button>
                </div>
                
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setShowLogoutConfirm(true)}
                    className="btn-ghost text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center space-x-2"
                  >
                    <LogOut size={18} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Data Management */}
            <div className="card-base card-padding-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Data Management</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-200">Export Your Data</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Download a copy of your data</p>
                  </div>
                  <button className="btn-secondary flex items-center space-x-2">
                    <Download size={16} />
                    <span>Export</span>
                  </button>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-200">Delete Account</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Permanently delete your account and data</p>
                  </div>
                  <button className="btn-error">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
            
            {/* Help & Support */}
            <div className="card-base card-padding-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Help & Support</h3>
              
              <div className="space-y-4">
                <button className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors text-left">
                  <div className="flex items-center space-x-3">
                    <HelpCircle className="text-purple-600 dark:text-purple-400" size={20} />
                    <span className="font-medium text-gray-800 dark:text-gray-200">FAQs</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </button>
                
                <button className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors text-left">
                  <div className="flex items-center space-x-3">
                    <MessageCircle className="text-purple-600 dark:text-purple-400" size={20} />
                    <span className="font-medium text-gray-800 dark:text-gray-200">Contact Support</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </button>
                
                <button className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors text-left">
                  <div className="flex items-center space-x-3">
                    <ShieldCheck className="text-purple-600 dark:text-purple-400" size={20} />
                    <span className="font-medium text-gray-800 dark:text-gray-200">Privacy Policy</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <LogoutConfirmationDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
      
      {showChatModal && (
        <ChatModal
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
          initialConversationId={initialChatId}
        />
      )}
      
      {showExpertVerification && (
        <ExpertVerification
          onClose={() => setShowExpertVerification(false)}
          onSubmit={handleExpertVerificationSubmit}
        />
      )}
    </div>
  );
};

export default Profile;